import { useState, useEffect, useRef } from "react";
import { Toaster, toast } from '@/components/ui/sonner';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";

// Components
import Layout from "@/components/Layout";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import CompleteRegistration from "@/pages/CompleteRegistration";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminHome from "@/pages/AdminHome";
import PhotoUpload from "@/pages/PhotoUpload";
import VehicleHistory from "@/pages/VehicleHistory";
import PlanningView from "@/pages/PlanningView";
import DemandesPage from "@/pages/DemandesPage";
import NotificationCenter from "@/pages/NotificationCenter";
import NotificationDebug from "@/pages/NotificationDebug";
import AdminEmployees from "@/pages/AdminEmployees";
import AdminVehicles from "@/pages/AdminVehicles";
import AdminPlanning from "@/pages/AdminPlanning";
import AdminPlanningIntegrity from "@/pages/AdminPlanningIntegrity";
import DeactivatedEmployeesHistory from "@/pages/DeactivatedEmployeesHistory";
import RecoveryGuide from "@/pages/RecoveryGuide";
import AdminPhotos from "@/pages/AdminPhotos";
import PendingApproval from "@/pages/PendingApproval";
import SiteManagement from "@/pages/SiteManagement";
import FormationsBinome from "@/pages/FormationsBinome";
import FormationsOnline from "@/pages/FormationsOnline";
import AdminWarnings from "@/pages/AdminWarnings";
import EmployeeProfile from "@/pages/EmployeeProfile";
import AdminEmployeeProfile from "@/pages/AdminEmployeeProfile";
import AdminValidations from "@/pages/AdminValidations";
import AdminVehicleDetails from "@/pages/AdminVehicleDetails";
import AdminStatsColisPrive from "@/pages/AdminStatsColisPrive";
import AdminStatsAmazon from "@/pages/AdminStatsAmazon";
import AdminComments from "@/pages/AdminComments";
import AdminDemandes from "@/pages/AdminDemandes";
import AdminCandidatures from "@/pages/AdminCandidatures";
import AdminCalculSalaire from "@/pages/AdminCalculSalaire";
import AdminTrialPeriods from "@/pages/AdminTrialPeriods";
import AdminEmails from "@/pages/AdminEmails";
import RHIntegrationTrackingPage from "@/pages/RHIntegrationTrackingPage";
import AdminRDV from "@/pages/AdminRDV";
import AdminDemenagement from "@/pages/AdminDemenagement";
import AdminLivraisonB2C from "@/pages/AdminLivraisonB2C";
import QuotePage from "@/pages/QuotePage";
import EnterpriseContactPage from "@/pages/EnterpriseContactPage";
import AdminQuotes from "@/pages/AdminQuotes";
import AdminReclamations from "@/pages/AdminReclamations";
import EmployeeReclamations from "@/pages/EmployeeReclamations";
import SuiviPDA from "@/pages/SuiviPDA";
import DevisPublic from "@/pages/DevisPublic";
import AdminDriverStatistics from "@/pages/AdminDriverStatistics";
import EmployeeStatistics from "@/components/EmployeeStatistics";
import AdminManagement from "@/pages/AdminManagement";
import FuelTracking from "@/pages/FuelTracking";
import KilometriqueTracking from "@/pages/KilometriqueTracking";
import AmazonPrestationTracking from "@/pages/AmazonPrestationTracking";
import InternalStaffSpace from "@/pages/InternalStaffSpace";
import AdminInternalHours from "@/pages/AdminInternalHours";
import OperationsWorkspace from "@/pages/OperationsWorkspace";
import GeoControlReview from "@/pages/GeoControlReview";
import { INTERNAL_ROLE, canManageInternal } from '@/components/internal-staff/shared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
// API doit être initialisée avant les modules de recette qui l'utilisent.
// Ces deux pages de recette sont exclues des builds de production.
const AdminGeoSimulation = process.env.NODE_ENV === 'development' ? require('@/pages/AdminGeoSimulation').default : null;
const GeoControlAcceptance = process.env.NODE_ENV === 'development' ? require('@/pages/GeoControlAcceptance').default : null;

// Helper component for redirects with dynamic params
const RedirectWithParams = ({ to, suffix, paramName }) => {
  const params = useParams();
  const paramValue = params[paramName];
  return <Navigate to={`${to}/${paramValue}${suffix}`} replace />;
};

// Helper function to get auth token (from localStorage for Safari compatibility)
export const getAuthToken = () => {
  return localStorage.getItem('session_token');
};

// Helper function to set auth token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('session_token', token);
  } else {
    localStorage.removeItem('session_token');
  }
};

// Helper function to clear auth data
export const clearAuthData = () => {
  localStorage.removeItem('session_token');
  sessionStorage.clear();
  document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

// Sync backend signature to avoid stale desktop sessions after URL/environment changes
export const syncAuthBackendSignature = () => {
  try {
    // Use API-origin signature to avoid false auth reset when frontend domain changes
    const signature = API || BACKEND_URL || window.location.origin;
    const key = 'auth_backend_signature';
    const previous = localStorage.getItem(key);
    if (previous && previous !== signature) {
      clearAuthData();
    }
    localStorage.setItem(key, signature);
  } catch (e) {
    // Ignore storage access errors
  }
};

// Setup axios interceptor to add Authorization header for Safari compatibility
axios.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Setup axios interceptor for 403 errors (deactivated accounts ONLY)
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 403) {
      const message = error.response?.data?.detail || '';
      if (typeof message === 'string' && message.startsWith('Accès limité à la gestion des dispatchers') && !window.location.pathname.startsWith('/operations/')) {
        window.location.assign('/operations/planning');
      }
      // ONLY handle deactivated account errors, not other 403s
      if (message === 'Compte désactivé. Contactez l\'administration.') {
        // Clear all auth data
        clearAuthData();
        
        // Redirect without alert
        console.error('Compte désactivé. Contactez l\'administration.');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    // Handle 401 errors - clear token and redirect
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isAuthMeCall = (error.config?.url || '').includes('/auth/me');
      const justAuthenticated = sessionStorage.getItem('just_authenticated');
      const isAuthEndpoint = (error.config?.url || '').includes('/auth/');
      if (currentPath !== '/login' && currentPath !== '/auth/callback') {
        // Avoid clearing auth data on transient auth/me race right after login
        if (!(isAuthMeCall && justAuthenticated) && !isAuthEndpoint) {
          clearAuthData();
          window.dispatchEvent(new CustomEvent('dlv:session-lost'));
        }
      }
    }
    // Lecture échouée (coupure, serveur indisponible) : ne jamais laisser croire qu'une liste vide est la réalité
    const method = (error.config?.method || 'get').toLowerCase();
    const status = error.response?.status;
    if (method === 'get' && !axios.isCancel(error) && (!error.response || status >= 500)) {
      toast.warning("Certaines données n'ont pas pu être chargées (connexion ou serveur). L'affichage peut être incomplet : actualisez dans un instant.",
        { id: 'global-load-failure', duration: 8000 });
    }
    return Promise.reject(error);
  }
);

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => toast.error('Connexion perdue : vos saisies non enregistrées restent affichées, ne fermez pas la page.', { id: 'net-offline', duration: Infinity }));
  window.addEventListener('online', () => { toast.dismiss('net-offline'); toast.success('Connexion rétablie.', { id: 'net-online' }); });
}

// Auth context
export const AuthContext = ({ children }) => {
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole, requiredStatus }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const checkingRef = useRef(false);

  useEffect(() => {
    syncAuthBackendSignature();

    // Server authority is checked even after a login or a route change.

    const checkAuth = async () => {
      if (checkingRef.current) return;
      checkingRef.current = true;

      try {
        // Add delay for cookie availability (important right after login on desktop browsers)
        const justAuth = sessionStorage.getItem('just_authenticated');
        if (justAuth) {
          await new Promise(r => setTimeout(r, 350));
          sessionStorage.removeItem('just_authenticated');
        } else {
          await new Promise(r => setTimeout(r, 150));
        }

        const tryAuthMe = async (useCredentials = true) => {
          const token = getAuthToken();
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          return axios.get(`${API}/auth/me`, {
            withCredentials: useCredentials,
            headers
          });
        };

        let response;
        try {
          response = await tryAuthMe(true);
        } catch (firstError) {
          // Retry once after short delay for desktop cookie propagation edge-cases
          if (firstError?.response?.status === 401) {
            await new Promise(r => setTimeout(r, 300));
            // Second attempt without cookie requirement (bearer token fallback)
            response = await tryAuthMe(false);
          } else {
            throw firstError;
          }
        }
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Auth check failed:", error);
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      } finally {
        checkingRef.current = false;
      }
    };

    checkAuth();
  }, [navigate, location.state, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Internal staff never render driver/admin screens, including direct URLs.
  if (user?.operations_manager && !location.pathname.startsWith('/operations/')) {
    return <Navigate to="/operations/planning" replace />;
  }
  if (location.pathname.startsWith('/operations/') && !user?.operations_manager && !canManageInternal(user)) {
    return <Navigate to={user?.role === 'admin' ? '/admin/b2b' : '/dashboard'} replace />;
  }
  if (user?.role === INTERNAL_ROLE) {
    if (!['/internal/planning', '/internal/schedule', '/internal/hours'].includes(location.pathname)) {
      return <Navigate to="/internal/planning" replace />;
    }
    if (user.status !== 'approved') {
      return <div className="p-8" data-testid="internal-account-pending">Compte en attente de validation.</div>;
    }
  }
  if (location.pathname === '/admin/b2b/internal-hours' && !canManageInternal(user)) {
    return <Navigate to={user?.role === 'admin' ? '/admin/b2b' : '/dashboard'} replace />;
  }

  // Check role requirement
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to={user?.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  // Block Colis Privé admin from accessing B2C, Déménagement, and Amazon routes
  if (user?.admin_level === 'colis_prive') {
    const blockedPaths = ['/admin/b2c', '/admin/demenagement', '/admin/b2b/presta/amazon', '/admin/b2b/presta/amazon-suivi'];
    const isBlocked = blockedPaths.some(p => location.pathname.startsWith(p));
    if (isBlocked) {
      return <Navigate to="/admin/b2b" replace />;
    }
  }

  // Full admin only route guard
  if (location.pathname === '/admin/b2b/suivi-integration-rh') {
    const isSuperAdmin = user?.is_super_admin === true || user?.email === 'transport@dlv-express.com';
    const isFullAdmin = user?.admin_level === 'full';
    if (!isSuperAdmin && !isFullAdmin) {
      return <Navigate to="/admin/b2b" replace />;
    }
  }

  // Check status requirement
  if (requiredStatus === "approved" && user?.status !== "approved") {
    if (!user?.has_cni || !user?.phone) {
      return <Navigate to="/complete-registration" replace />;
    }
    return <Navigate to="/pending-approval" replace />;
  }

  // Pass user to children
  return children({ user, setUser });
};

function AppRouter() {
  const location = useLocation();

  // Handle session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/devis" element={<QuotePage />} />
      <Route path="/devis-demenagement" element={<DevisPublic />} />
      <Route path="/contact-entreprise" element={<EnterpriseContactPage />} />
      
      <Route path="/complete-registration" element={
        <ProtectedRoute>
          {({ user }) => <CompleteRegistration user={user} />}
        </ProtectedRoute>
      } />
      
      <Route path="/pending-approval" element={
        <ProtectedRoute>
          {({ user }) => <PendingApproval user={user} />}
        </ProtectedRoute>
      } />
      
      {/* Employee Routes */}
      <Route path="/operations/:view" element={<ProtectedRoute requiredRole="admin">{({ user }) => <OperationsWorkspace user={user} />}</ProtectedRoute>} />
      <Route path="/internal/planning" element={<ProtectedRoute requiredRole={INTERNAL_ROLE}>{({ user }) => <InternalStaffSpace user={user} />}</ProtectedRoute>} />
      <Route path="/internal/hours" element={<ProtectedRoute requiredRole={INTERNAL_ROLE}>{({ user }) => <InternalStaffSpace user={user} view="hours" />}</ProtectedRoute>} />
      <Route path="/internal/schedule" element={<ProtectedRoute requiredRole={INTERNAL_ROLE}>{({ user }) => <InternalStaffSpace user={user} view="schedule" />}</ProtectedRoute>} />
      <Route path="/admin/b2b/internal-hours" element={<ProtectedRoute requiredRole="admin">{({ user }) => <AdminInternalHours user={user} />}</ProtectedRoute>} />
      {process.env.NODE_ENV === 'development' && <Route path="/admin/b2b/geolocation-simulation" element={<ProtectedRoute requiredRole="admin">{({ user }) => <AdminGeoSimulation user={user} />}</ProtectedRoute>} />}
      {process.env.NODE_ENV === 'development' && <Route path="/admin/b2b/geolocation-acceptance" element={<ProtectedRoute requiredRole="admin">{({ user }) => <GeoControlAcceptance user={user} />}</ProtectedRoute>} />}
      <Route path="/operations/location-exceptions" element={<ProtectedRoute requiredRole="admin">{({ user }) => <GeoControlReview user={user} />}</ProtectedRoute>} />
      <Route path="/dashboard" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => <EmployeeDashboard user={user} />}
        </ProtectedRoute>
      } />

      <Route path="/dashboard-v2" element={
        <ProtectedRoute requiredStatus="approved">
          {() => <Navigate to="/dashboard" replace />}
        </ProtectedRoute>
      } />

      <Route path="/photo-upload" element={<Navigate to="/photos" replace />} />
      <Route path="/vehicle-history" element={<Navigate to="/history" replace />} />
      
      <Route path="/mes-statistiques" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => (
            <Layout user={user}>
              <EmployeeStatistics user={user} />
            </Layout>
          )}
        </ProtectedRoute>
      } />
      
      <Route path="/photos" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => <PhotoUpload user={user} />}
        </ProtectedRoute>
      } />
      
      <Route path="/history" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => <VehicleHistory user={user} />}
        </ProtectedRoute>
      } />
      
      <Route path="/planning" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => <PlanningView user={user} />}
        </ProtectedRoute>
      } />
      
      <Route path="/demandes" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => <DemandesPage user={user} />}
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user, setUser }) => <EmployeeProfile user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/notifications" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => <NotificationCenter user={user} />}
        </ProtectedRoute>
      } />
      
      {/* Employee Reclamations - Colis Privé */}
      <Route path="/mes-reclamations" element={
        <ProtectedRoute requiredStatus="approved">
          {({ user }) => <EmployeeReclamations user={user} />}
        </ProtectedRoute>
      } />
      
      {/* ====== ADMIN HOME - Activity Selection ====== */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminHome user={user} />}
        </ProtectedRoute>
      } />
      
      {/* ====== B2B MODULE ROUTES ====== */}
      <Route path="/admin/b2b" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDashboard user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/employees" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminEmployees user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      <Route path="/admin/b2b/employees/deactivated-history" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <DeactivatedEmployeesHistory user={user} />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/vehicles" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminVehicles user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/planning" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminPlanning user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/planning/integrity" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminPlanningIntegrity user={user} />}
        </ProtectedRoute>
      } />

      <Route path="/admin/planning/recovery-guide" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <RecoveryGuide user={user} />}
        </ProtectedRoute>
      } />

      <Route path="/admin/b2b/photos" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminPhotos user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/sites" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <SiteManagement user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/formations-binome" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <FormationsBinome user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/formations-online" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <FormationsOnline user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />

      <Route path="/admin/b2b/rdv" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminRDV user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/warnings" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminWarnings user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/employees/:userId/profile" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminEmployeeProfile user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/validations" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminValidations user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/vehicle-management" element={<Navigate to="/admin/b2b/vehicles" replace />} />
      
      <Route path="/admin/b2b/vehicles/:vehicleId/details" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminVehicleDetails user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/presta/colis-prive" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminStatsColisPrive user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/presta/amazon" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminStatsAmazon user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />

      <Route path="/admin/b2b/presta/amazon-suivi" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AmazonPrestationTracking user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/comments" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminComments user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/demandes" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemandes user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/candidatures" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminCandidatures user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/calcul-salaire" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminCalculSalaire user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/trial-periods" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminTrialPeriods user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />

      <Route path="/admin/b2b/suivi-integration-rh" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <RHIntegrationTrackingPage user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/emails" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminEmails user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      {/* ====== B2C MODULE ROUTES ====== */}
      <Route path="/admin/b2c" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminLivraisonB2C user={user} activeModule="b2c" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2c/demandes" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminLivraisonB2C user={user} activeModule="b2c" view="demandes" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2c/devis" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminLivraisonB2C user={user} activeModule="b2c" view="devis" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2c/missions" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminLivraisonB2C user={user} activeModule="b2c" view="missions" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2c/planning" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminLivraisonB2C user={user} activeModule="b2c" view="planning" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2c/clients" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminLivraisonB2C user={user} activeModule="b2c" view="clients" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2c/facturation" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminLivraisonB2C user={user} activeModule="b2c" view="facturation" />}
        </ProtectedRoute>
      } />
      
      {/* ====== DEMENAGEMENT MODULE ROUTES ====== */}
      <Route path="/admin/demenagement" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemenagement user={user} activeModule="demenagement" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/demenagement/demandes" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemenagement user={user} activeModule="demenagement" view="demandes" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/demenagement/devis" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemenagement user={user} activeModule="demenagement" view="devis" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/demenagement/missions" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemenagement user={user} activeModule="demenagement" view="missions" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/demenagement/planning" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemenagement user={user} activeModule="demenagement" view="planning" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/demenagement/inventaires" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemenagement user={user} activeModule="demenagement" view="inventaires" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/demenagement/clients" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDemenagement user={user} activeModule="demenagement" view="clients" />}
        </ProtectedRoute>
      } />
      
      {/* ====== QUOTES MANAGEMENT ====== */}
      <Route path="/admin/b2b/quotes" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminQuotes user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      {/* ====== RECLAMATIONS MODULE ====== */}
      <Route path="/admin/b2b/reclamations" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminReclamations user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      {/* ====== STATISTIQUES LIVREURS MODULE ====== */}
      <Route path="/admin/b2b/statistiques-livreurs" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminDriverStatistics user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      {/* ====== SUIVI PDA MODULE ====== */}
      <Route path="/admin/b2b/suivi-pda" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <SuiviPDA user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      <Route path="/admin/b2b/gestion-admins" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <AdminManagement user={user} />}
        </ProtectedRoute>
      } />
      
      {/* ====== FUEL TRACKING MODULE ====== */}
      <Route path="/admin/b2b/carburant" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <FuelTracking user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      <Route path="/admin/b2b/kilometrique" element={
        <ProtectedRoute requiredRole="admin" requiredStatus="approved">
          {({ user }) => <KilometriqueTracking user={user} activeModule="b2b" />}
        </ProtectedRoute>
      } />
      
      {/* ====== LEGACY ROUTES (redirects to new structure) ====== */}
      <Route path="/admin/employees" element={<Navigate to="/admin/b2b/employees" replace />} />
      <Route path="/admin/vehicles" element={<Navigate to="/admin/b2b/vehicles" replace />} />
      <Route path="/admin/planning" element={<Navigate to="/admin/b2b/planning" replace />} />
      <Route path="/admin/photos" element={<Navigate to="/admin/b2b/photos" replace />} />
      <Route path="/admin/sites" element={<Navigate to="/admin/b2b/sites" replace />} />
      <Route path="/admin/formations-binome" element={<Navigate to="/admin/b2b/formations-binome" replace />} />
      <Route path="/admin/formations-online" element={<Navigate to="/admin/b2b/formations-online" replace />} />
      <Route path="/admin/rdv" element={<Navigate to="/admin/b2b/rdv" replace />} />
      <Route path="/admin/warnings" element={<Navigate to="/admin/b2b/warnings" replace />} />
      <Route path="/admin/validations" element={<Navigate to="/admin/b2b/validations" replace />} />
      <Route path="/admin/vehicle-management" element={<Navigate to="/admin/b2b/vehicle-management" replace />} />
      <Route path="/admin/presta/colis-prive" element={<Navigate to="/admin/b2b/presta/colis-prive" replace />} />
      <Route path="/admin/presta/amazon" element={<Navigate to="/admin/b2b/presta/amazon" replace />} />
      <Route path="/admin/presta/amazon-suivi" element={<Navigate to="/admin/b2b/presta/amazon-suivi" replace />} />
      <Route path="/admin/comments" element={<Navigate to="/admin/b2b/comments" replace />} />
      <Route path="/admin/demandes" element={<Navigate to="/admin/b2b/demandes" replace />} />
      <Route path="/admin/candidatures" element={<Navigate to="/admin/b2b/candidatures" replace />} />
      <Route path="/admin/calcul-salaire" element={<Navigate to="/admin/b2b/calcul-salaire" replace />} />
      <Route path="/admin/trial-periods" element={<Navigate to="/admin/b2b/trial-periods" replace />} />
      <Route path="/admin/emails" element={<Navigate to="/admin/b2b/emails" replace />} />
      <Route path="/admin-emails" element={<Navigate to="/admin/b2b/emails" replace />} />
      <Route path="/admin/livraison-b2c" element={<Navigate to="/admin/b2c" replace />} />
      <Route path="/admin/employees/:userId/profile" element={<RedirectWithParams to="/admin/b2b/employees" suffix="/profile" paramName="userId" />} />
      <Route path="/admin/vehicles/:vehicleId/details" element={<RedirectWithParams to="/admin/b2b/vehicles" suffix="/details" paramName="vehicleId" />} />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors closeButton />
    </BrowserRouter>
  );
}

export default App;
